import { type ReactNode } from "react";
import { Button } from "./button";

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Optional second action rendered as a secondary button beside the primary
   * one — e.g. a filtered-empty list offering both "Clear filters" (primary)
   * and "New group" (secondary). Only renders when the primary action is also
   * present.
   */
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  /**
   * Layout variant. `"page"` (default) renders the centered, vertically
   * padded full-page empty state. `"inline"` renders a compact,
   * left-aligned variant suitable for use inside list-inside-card sections
   * where the surrounding card already provides the visual framing.
   */
  variant?: "page" | "inline";
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  variant = "page",
}: EmptyStateProps) {
  const isInline = variant === "inline";
  const wrapperClass = isInline
    ? "py-4 text-sm"
    : "flex flex-col items-center justify-center py-12";
  const titleClass = isInline
    ? "font-medium text-on-surface"
    : "text-lg font-medium text-on-surface";
  return (
    <div className={wrapperClass}>
      {icon && !isInline && (
        <div className="mb-4 text-muted" style={{ width: 48, height: 48 }}>
          {icon}
        </div>
      )}
      <p className={titleClass}>{title}</p>
      {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      {actionLabel && onAction && (
        <div className={`flex items-center gap-3 ${isInline ? "mt-2" : "mt-4"}`}>
          <Button variant={isInline ? "ghost" : "primary"} onClick={onAction}>
            {actionLabel}
          </Button>
          {secondaryActionLabel && onSecondaryAction && (
            <Button variant="secondary" onClick={onSecondaryAction}>
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
